/**
 * The report's Metadata block — what it took to make this answer.
 *
 * Sits at the top of the "SKJV It is Written Report" so a training
 * analysis reads the run's conditions before its words: how long it took,
 * which LLMs ran and how often, whether a re-roll or a guard fired, and
 * what the deterministic validator found. Most bad answers are explained
 * by a line in here.
 *
 * Pure: the caller may pass `client` (tests do); the default reads the
 * running app.
 */

import type { CommentaryRunMeta, RunModel } from '@skjv/common';
import type { ReportRunMeta } from '@state/chat';
import { formatDuration } from '../format-duration';
import { isDesktop, getDesktop } from '@/desktop/desktop';
import { desktopUpdateStore } from '@/desktop/desktop-update-state';

export interface ReportClient {
  surface: 'web' | 'desktop';
  /** Host OS, desktop only. */
  platform?: string;
  /** Desktop build, when the shell has reported its updater state. */
  version?: string;
}

export type { ReportRunMeta };

export function describeClient(): ReportClient {
  if (!isDesktop()) return { surface: 'web' };
  return {
    surface: 'desktop',
    platform: getDesktop()?.platform,
    version: desktopUpdateStore.state?.current,
  };
}

function describeGuard(run: CommentaryRunMeta): string {
  const { outcome, violations } = run.guard;
  if (violations.length === 0) return outcome;
  return `${outcome} — ${violations.join('; ')}`;
}

function describeValidation(run: CommentaryRunMeta): string | null {
  const v = run.validation;
  if (!v) return null;
  const notes: string[] = [];
  notes.push(v.unlistedCrossRefs.length === 0
    ? 'cross-refs all in allow-list'
    : `unlisted cross-refs: ${v.unlistedCrossRefs.join(', ')}`);
  notes.push(v.missingCrossRefFloor ? 'cross-ref floor MISSED' : 'cross-ref floor met');
  if (v.fabricatedQuotes.length > 0) {
    notes.push(`fabricated quotes stripped: ${v.fabricatedQuotes.join(' | ')}`);
  }
  if (v.badStrongs.length > 0) notes.push(`bad Strong's: ${v.badStrongs.join(', ')}`);
  if (v.unlistedStrongs.length > 0) {
    notes.push(`unlisted Strong's: ${v.unlistedStrongs.join(', ')}`);
  }
  if (v.stockClosers.length > 0) notes.push(`stock closers: ${v.stockClosers.join(' | ')}`);
  if (v.duplicateParagraphs > 0) notes.push(`${v.duplicateParagraphs} duplicate paragraph(s)`);
  return notes.join('; ');
}

function describeClientLine(client: ReportClient): string {
  if (client.surface === 'web') return 'web';
  const parts = [client.version, client.platform].filter(Boolean);
  return parts.length > 0 ? `desktop ${parts.join(' · ')}` : 'desktop';
}

/**
 * Metadata as report bullet lines. Never throws on a partial record — a
 * run from an older build simply contributes fewer lines.
 */
export function buildMetadataLines(
  meta: ReportRunMeta,
  reportedAt: string,
  client: ReportClient = describeClient(),
): string[] {
  const { run, clientElapsedMs } = meta;
  const lines: string[] = [];

  lines.push(`- Reported: ${reportedAt}`);
  if (run) lines.push(`- Generated: ${run.generatedAt}`);

  const durations = [
    run ? `${formatDuration(run.durationMs)} server` : null,
    clientElapsedMs != null ? `${formatDuration(clientElapsedMs)} wall (Generate → rendered)` : null,
  ].filter(Boolean);
  if (durations.length > 0) lines.push(`- Duration: ${durations.join(' · ')}`);

  lines.push(`- Client: ${describeClientLine(client)}`);

  if (!run) {
    lines.push('- Run detail: _not captured — generated before the run record existed._');
    return lines;
  }

  lines.push(`- Mode: ${run.mode} · ${run.passageReference} (${run.verseCount} verses)`);
  const modelList = run.models
    .map((m: RunModel) => `${m.model} — ${m.role}, ${m.calls} call${m.calls === 1 ? '' : 's'}`)
    .join(' · ');
  lines.push(`- Models: ${modelList}`);

  if (run.inference) {
    const i = run.inference;
    lines.push(
      `- Inference: ${i.promptTokens.toLocaleString()} prompt → ` +
      `${i.completionTokens.toLocaleString()} completion tokens, ` +
      `${i.tokensPerSecond.toLocaleString()} tok/s, ` +
      `${formatDuration(i.loadDurationMs)} model load`,
    );
  }

  lines.push(
    `- Rolls: ${run.rolls.generations} generation(s) — ` +
    `${run.rolls.anchorRerolls} anchor re-roll(s), ${run.rolls.guardReasks} guard re-ask(s)`,
  );
  lines.push(`- Guards: ${describeGuard(run)}`);

  const validation = describeValidation(run);
  if (validation) lines.push(`- Validator: ${validation}`);

  if (run.verseAnchors) {
    const a = run.verseAnchors;
    const drift = [
      a.outOfRange.length > 0 ? `out of range: ${a.outOfRange.join(', ')}` : null,
      a.nonAscending ? 'non-ascending' : null,
    ].filter(Boolean);
    lines.push(
      `- Verse anchors: ${a.anchoredBlocks}/${a.totalBlocks} blocks, ` +
      `coverage ${Math.round(a.coverageRatio * 100)}%` +
      (drift.length > 0 ? ` — ${drift.join('; ')}` : ''),
    );
  }

  lines.push(`- Prompt: ${run.promptChars.toLocaleString()} chars · ` +
    `${run.allowListSize} cross-ref(s) offered · ` +
    `${run.readerThoughtsChars} chars of reader thoughts`);
  lines.push(`- Engine: ${run.engine.hostname} → ${run.engine.ollamaHost}`);

  return lines;
}
